"use client";

import { useEffect, useState } from "react";
import { Bookmark, Link2, Plus } from "lucide-react";
import { useAccountModal } from "@/contexts/account-modal-context";
import { useAuth } from "@/contexts/auth-context";
import { track } from "@/lib/analytics";
import {
  addLibraryEntry,
  deleteLibraryEntry,
  getPiece,
  type CatalogPiece,
  type LibraryEntryItem,
} from "@/lib/api/api-client";
import { useLibraryFeedback } from "./library-feedback";
import { useLibraryOverview } from "./library-overview";
import { useLibraryActions } from "./use-library-actions";

/**
 * « Ajouter à mes morceaux » et « Copier le lien de partage », sur la fiche.
 *
 * # Un vrai bouton bascule, et non un texte qui remplace le bouton
 *
 * `aria-pressed` dit l'état, et c'est ce qui rend le geste annulable **au même
 * endroit** : on clique une fois de trop, on reclique. Un libellé qui se
 * changerait en « Dans mes morceaux » sans rester actionnable laisserait la
 * personne chercher où défaire ce qu'elle vient de faire.
 *
 * # Le bouton naît pressé, mais il faut aller le demander
 *
 * `LibraryEntryRecorder` crée la ligne à la première ouverture du morceau : qui
 * a ouvert dix morceaux en a déjà dix dans sa liste, et le bouton doit le
 * savoir. Or la fiche est rendue par le serveur Next, qui lit l'API **sans
 * jeton** — `piece.inLibrary` y vaut donc toujours faux. D'où cette relecture
 * côté client dès qu'une session existe : une requête, une seule, et seulement
 * pour quelqu'un de connecté.
 *
 * # Le retrait n'est offert qu'après un ajout fait ici
 *
 * `deleteLibraryEntry` prend l'identifiant de l'**entrée**, que
 * `CatalogPieceDetail` ne porte pas : la fiche sait que le morceau est rangé,
 * pas sous quelle ligne. On l'apprend en l'ajoutant — `addLibraryEntry` rend
 * l'entrée complète — donc la bascule est réversible exactement dans le cas où
 * elle sert, celui du clic qu'on vient de faire. Ailleurs, le retrait vit dans
 * « Mes morceaux », où l'entrée est sous la main.
 *
 * # Jamais grisé pour un anonyme
 *
 * C'est le meilleur moment d'inscription du produit : la demande vient de la
 * personne, qui vient de choisir un morceau précis.
 */

export function PieceSheetActions({ piece }: { piece: CatalogPiece }) {
  const { isAuthenticated } = useAuth();
  const { openAccountModal } = useAccountModal();
  const { notify } = useLibraryFeedback();
  const { refresh } = useLibraryOverview();
  const { busy: sharing, copyShareLink } = useLibraryActions();

  const [inLibrary, setInLibrary] = useState(piece.inLibrary);
  /** L'entrée, connue seulement quand c'est nous qui venons de la créer. */
  const [entry, setEntry] = useState<LibraryEntryItem | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setInLibrary(false);
      setEntry(null);

      return;
    }

    let cancelled = false;

    getPiece(piece.id)
      .then((detail) => {
        if (!cancelled) {
          setInLibrary(detail.piece.inLibrary);
        }
      })
      .catch(() => {
        // La fiche reste juste sans cette précision : l'ajout est idempotent et
        // répondra « Tu as déjà ce morceau » le cas échéant.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, piece.id]);

  const add = () => {
    if (!isAuthenticated) {
      openAccountModal("library_add");

      return;
    }

    setBusy(true);

    addLibraryEntry({ pieceId: piece.id })
      .then((result) => {
        setInLibrary(true);
        setEntry(result.item);
        refresh();

        // Même règle que dans `use-library-actions` : `created` seulement, un
        // `200` ne fait que retrouver une ligne déjà écrite par une ouverture.
        if (result.created) {
          track({
            name: "library_item_added",
            properties: { source: result.item.source, pieceId: piece.id },
          });
        }

        notify({
          message: result.created
            ? "Ajouté à mes morceaux."
            : "Tu as déjà ce morceau.",
        });
      })
      .catch(() => {
        notify({ message: "Ce morceau n'a pas pu être ajouté.", failed: true });
      })
      .finally(() => setBusy(false));
  };

  const undo = () => {
    if (entry === null) {
      return;
    }

    setBusy(true);

    deleteLibraryEntry(entry.id)
      .then(() => {
        setInLibrary(false);
        setEntry(null);
        refresh();
        // Le seul retrait immédiat de la bibliothèque : ailleurs il est
        // différé, et c'est l'expiration du « Annuler » qui le compte.
        track({
          name: "library_item_removed",
          properties: { pieceId: piece.id },
        });
        notify({ message: "Retiré de mes morceaux." });
      })
      .catch(() => {
        notify({ message: "Le morceau n'a pas pu être retiré.", failed: true });
      })
      .finally(() => setBusy(false));
  };

  const reversible = inLibrary && entry !== null;

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {inLibrary && !reversible ? (
        /*
          Rangé, mais pas par ce clic-ci : la marque dit l'état sans promettre
          un geste qu'on ne sait pas rendre ici. Voir l'en-tête du fichier.
        */
        <span
          className="inline-flex h-11 items-center gap-2 rounded-xl px-5 text-[13.5px] font-medium text-accent-bright"
          style={{
            backgroundColor: "var(--color-accent-subtle)",
            border: "1px solid var(--color-violet-glow)",
          }}
        >
          <Bookmark size={15} strokeWidth={1.8} aria-hidden />
          Dans mes morceaux
        </span>
      ) : (
        <button
          type="button"
          aria-pressed={inLibrary}
          disabled={busy}
          onClick={inLibrary ? undo : add}
          className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl px-5 text-[13.5px] font-medium transition-all duration-150 disabled:cursor-default disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={
            inLibrary
              ? {
                  backgroundColor: "var(--color-accent-subtle)",
                  border: "1px solid var(--color-violet-glow)",
                  color: "var(--color-accent-bright)",
                }
              : {
                  border: "1px solid var(--color-border-strong)",
                  color: "var(--color-text-secondary)",
                }
          }
        >
          {inLibrary ? (
            <Bookmark size={15} strokeWidth={1.8} aria-hidden />
          ) : (
            <Plus size={15} strokeWidth={1.8} aria-hidden />
          )}
          {inLibrary ? "Dans mes morceaux" : "Ajouter à mes morceaux"}
        </button>
      )}

      {/*
        Le partage n'apparaît que sur un morceau qu'on a : on ne partage que ce
        qu'on possède, et l'API le refuse en `403` sinon.
      */}
      {inLibrary && (
        <button
          type="button"
          disabled={sharing}
          onClick={() => {
            // `catalog` : la fiche est servie par `/library/[slug]`, et un slug
            // n'existe que sur une pièce du catalogue. Ni un import ni un
            // morceau reçu n'atteint cet écran.
            copyShareLink(piece, "catalog");
          }}
          className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl px-5 text-[13.5px] font-medium text-text-secondary transition-colors duration-150 hover:bg-white/[0.06] disabled:cursor-default disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{ border: "1px solid var(--color-border-strong)" }}
        >
          <Link2 size={15} strokeWidth={1.8} aria-hidden />
          Copier le lien de partage
        </button>
      )}
    </div>
  );
}
